<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  fetchEra,
  fetchLeaderboard,
  type EraView,
  type LeaderboardView,
  type LeaderboardRow,
} from '../game/rankings-api';
import { BG, rankMedal } from '../game/art';

const loading = ref(true);
const error = ref('');

const era = ref<EraView | null>(null);
const board = ref<LeaderboardView | null>(null);
const category = ref('era-points');
const boardLoading = ref(false);

const fmt = (n: number): string => n.toLocaleString();

/** Label of the active category — the score's unit line on the podium. */
const activeCatLabel = computed(
  () => board.value?.categories.find((c) => c.key === category.value)?.label ?? '',
);

// ── Live era clock ──────────────────────────────────────────────────────────
const now = ref(Date.now());
let tick: number | undefined;
onMounted(() => { tick = window.setInterval(() => { now.value = Date.now(); }, 1000); });
onUnmounted(() => { if (tick) window.clearInterval(tick); });

const eraClock = computed(() => {
  if (!era.value) return '';
  const ms = new Date(era.value.era.endsAt).getTime() - now.value;
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = String(Math.floor((s % 86400) / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');
  return d > 0 ? `${d}d ${h}:${m}:${sec}` : `${h}:${m}:${sec}`;
});

// ── The tier ladder — full EraView.tiers, finally on display ────────────────
const ladder = computed(() => {
  if (!era.value) return [];
  return [...era.value.tiers].sort((a, b) => a.minPoints - b.minPoints);
});
/** Nodes sit at EQUAL intervals (thresholds are wildly uneven — a linear
 *  scale piles the early tiers into the first 8% of the rail). The player
 *  marker interpolates piecewise between the two thresholds it sits between,
 *  so geometry stays honest per segment. */
const tierPctAt = (ti: number) =>
  ladder.value.length > 1 ? Math.round((ti / (ladder.value.length - 1)) * 100) : 0;
const railPct = computed(() => {
  const l = ladder.value;
  if (!era.value || l.length < 2) return 0;
  const pts = era.value.player.rankPoints;
  if (pts >= l[l.length - 1].minPoints) return 100;
  let i = 0;
  while (i < l.length - 1 && pts >= l[i + 1].minPoints) i++;
  const lo = l[i].minPoints, hi = l[i + 1].minPoints;
  const frac = hi > lo ? (pts - lo) / (hi - lo) : 0;
  return Math.round(((i + frac) / (l.length - 1)) * 100);
});
const reachedTier = (min: number) => (era.value?.player.rankPoints ?? 0) >= min;

// ── Podium & ledger split ───────────────────────────────────────────────────
const podium = computed<LeaderboardRow[]>(() => board.value?.top.slice(0, 3) ?? []);
/** Podium display order: silver | gold | bronze. */
const podiumOrder = computed(() => {
  const p = podium.value;
  return [p[1], p[0], p[2]].filter(Boolean) as LeaderboardRow[];
});
const ledger = computed(() => board.value?.top.slice(3) ?? []);
const meInTop = computed(() => !!board.value?.top.some((r) => r.isMe));
/** …, 635, 636(me), 637 — the server sends this only when I rank past `top`. */
const around = computed<LeaderboardRow[]>(() => board.value?.around ?? []);
/** No window and not in `top` = no standing on this board yet. */
const unranked = computed(() => !!board.value && !meInTop.value && !around.value.length);

async function loadBoard(cat: string) {
  category.value = cat;
  boardLoading.value = true;
  error.value = '';
  try {
    board.value = await fetchLeaderboard(cat);
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    boardLoading.value = false;
  }
}

onMounted(async () => {
  try {
    era.value = await fetchEra();
    await loadBoard('era-points');
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section>
    <!-- banner -->
    <div class="corner-frame relative rounded-lg overflow-hidden mb-5 h-36">
      <img :src="BG.rankings" alt="" class="w-full h-full object-cover" />
      <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.25),rgba(10,7,20,0.94))"></div>
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="absolute bottom-3 left-4">
        <h1 class="font-heading text-2xl text-gold">Rankings</h1>
        <span class="font-label uppercase tracking-wider text-sm text-text-dim block">The city keeps score</span>
      </div>
    </div>

    <p v-if="loading" class="text-text-dim">Counting the bodies…</p>
    <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>

    <div v-if="!loading" class="grid gap-8">
      <!-- ── THE ERA — tier ladder ─────────────────────────────────── -->
      <div v-if="era" class="rk-era corner-frame relative">
        <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
        <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>

        <div class="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div class="flex items-center gap-3">
            <h2 class="font-heading text-xl text-gold">{{ era.era.name }}</h2>
            <span class="rk-chip">Era {{ era.era.number }}</span>
            <span class="rk-chip">Ends in <b class="text-gold ml-1 tabular-nums">{{ eraClock }}</b></span>
          </div>
          <div class="text-right">
            <span class="font-heading text-2xl text-gold-bright tabular-nums">{{ fmt(era.player.rankPoints) }}</span>
            <span class="font-label uppercase tracking-[0.18em] text-[10px] text-text-dim ml-2">era points</span>
          </div>
        </div>
        <p class="font-body italic text-xs text-text-dim mb-6">
          Standing: <span class="text-text not-italic">{{ era.player.tier }}</span>
          <template v-if="era.player.nextTier">
            — {{ fmt(era.player.pointsToNext) }} points from <span class="text-gold">{{ era.player.nextTier }}</span>
          </template>
          <template v-else> — <span class="text-gold">nobody stands higher</span></template>
        </p>

        <!-- the ladder rail — horizontal on tablet/desktop -->
        <div class="rk-rail-wrap hidden sm:block">
          <div class="rk-rail">
            <div class="rk-rail-fill" :style="{ width: railPct + '%' }"></div>
            <!-- player marker -->
            <div class="rk-you" :style="{ left: railPct + '%' }" :title="`You — ${fmt(era.player.rankPoints)} points`"></div>
            <!-- tier nodes -->
            <div
              v-for="(t, ti) in ladder" :key="t.tierName"
              class="rk-node" :class="{ 'rk-node-hit': reachedTier(t.minPoints), 'rk-node-alt': ti % 2 === 1, 'rk-node-first': ti === 0, 'rk-node-last': ti === ladder.length - 1 }"
              :style="{ left: tierPctAt(ti) + '%' }"
            >
              <span class="rk-node-diamond"></span>
              <span class="rk-node-name" :class="reachedTier(t.minPoints) ? 'text-gold' : 'text-text-dim'">{{ t.tierName }}</span>
              <span class="rk-node-req tabular-nums">{{ fmt(t.minPoints) }}</span>
              <!-- tier reward tooltip -->
              <span class="rk-node-tip">
                <b class="text-gold">{{ t.tierName }}</b>
                <span v-if="t.rewardSoft" class="block">{{ fmt(t.rewardSoft) }} cash</span>
                <span v-if="t.rewardPremium" class="block text-gold-bright">{{ t.rewardPremium }} gold bars</span>
                <span v-if="t.rewardXp" class="block text-purple-light">{{ fmt(t.rewardXp) }} XP</span>
                <span class="block italic text-text-dim">paid when the era closes</span>
              </span>
            </div>
          </div>
        </div>

        <!-- mobile: the same ladder as a vertical list (no sideways scroll) -->
        <ul class="rk-vlad sm:hidden">
          <li
            v-for="t in ladder" :key="'v-' + t.tierName"
            class="rk-vrow" :class="{ 'rk-vrow-cur': t.tierName === era.player.tier }"
          >
            <span class="rk-vdot" :class="{ 'rk-vdot-hit': reachedTier(t.minPoints) }"></span>
            <div class="min-w-0 flex-1">
              <div class="rk-vname" :class="reachedTier(t.minPoints) ? 'text-gold' : 'text-text-dim'">
                {{ t.tierName }}
                <span v-if="t.tierName === era.player.tier" class="rk-vyou">You</span>
              </div>
              <div v-if="t.rewardSoft || t.rewardPremium || t.rewardXp" class="rk-vrew">
                <span v-if="t.rewardSoft">{{ fmt(t.rewardSoft) }} cash</span>
                <span v-if="t.rewardPremium" class="text-gold-bright">· {{ t.rewardPremium }} bars</span>
                <span v-if="t.rewardXp" class="text-purple-light">· {{ fmt(t.rewardXp) }} XP</span>
              </div>
            </div>
            <span class="rk-vreq tabular-nums">{{ fmt(t.minPoints) }}</span>
          </li>
        </ul>
      </div>

      <!-- ── LEADERBOARD ───────────────────────────────────────────── -->
      <div>
        <div class="flex items-center justify-between gap-3 mb-3">
          <div class="flex items-baseline gap-3">
            <h2 class="font-heading text-xl text-gold">The ledger of names</h2>
            <span class="h-px w-24 self-center" style="background:linear-gradient(90deg,var(--color-border),transparent)"></span>
          </div>
          <div v-if="board" class="rk-chip">
            Your mark <b class="text-gold ml-1">#{{ board.me.rank }}</b><span class="text-text-dim ml-1">of {{ fmt(board.me.total) }}</span>
          </div>
        </div>

        <!-- category tabs -->
        <div v-if="board" class="flex flex-wrap gap-4 mb-5 border-b border-border/60">
          <button
            v-for="c in board.categories" :key="c.key"
            @click="loadBoard(c.key)"
            :class="['tab-deco', category === c.key && 'tab-deco-active']"
          >{{ c.label }}</button>
        </div>

        <p v-if="boardLoading" class="font-body text-text-dim text-sm">Turning the pages…</p>

        <template v-else-if="board">
          <!-- TOP THREE — one compact banner strip: [ II | I | III ] -->
          <div v-if="podium.length" class="rk-top3">
            <div
              v-for="row in podiumOrder" :key="row.userId"
              class="rk-cell"
              :class="[`rk-cell-${row.rank}`, { 'rk-cell-me': row.isMe }]"
            >
              <div class="rk-medal" :class="`rk-medal-${row.rank}`">
                <img v-if="rankMedal(row.rank)" :src="rankMedal(row.rank)" :alt="'#' + row.rank" class="w-full h-full object-contain" />
              </div>
              <div class="min-w-0">
                <div class="rk-cell-name" :class="row.rank === 1 ? 'text-gold-bright' : 'text-text'">
                  {{ row.username ?? 'Unknown' }}
                  <span v-if="row.isMe" class="rk-you-pill">You</span>
                </div>
                <div class="rk-cell-score" :class="row.rank === 1 ? 'text-gold' : 'text-gold/75'">
                  {{ fmt(row.value) }} <span class="rk-cell-unit">{{ activeCatLabel }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- LEDGER rows 4+ — tight account-book lines with dot leaders -->
          <div class="rk-book">
            <div
              v-for="row in ledger" :key="row.userId"
              class="rk-line" :class="{ 'rk-line-me': row.isMe }"
            >
              <span class="rk-no">No. {{ String(row.rank).padStart(2, '0') }}</span>
              <span class="rk-name">
                {{ row.username ?? 'Unknown' }}
                <span v-if="row.isMe" class="rk-you-pill">You</span>
              </span>
              <span class="rk-leader" aria-hidden="true"></span>
              <span class="rk-val">{{ fmt(row.value) }}</span>
            </div>

            <!-- below the fold: the book skips ahead, then me between my
                 immediate neighbours — …, 635, 636 (you), 637 -->
            <template v-if="around.length">
              <div class="rk-skip" aria-label="ranks omitted">
                <span class="rk-skip-dots">&#8943;</span>
              </div>
              <div
                v-for="row in around" :key="row.userId"
                class="rk-line" :class="{ 'rk-line-me': row.isMe }"
              >
                <span class="rk-no">No. {{ String(row.rank).padStart(2, '0') }}</span>
                <span class="rk-name">
                  {{ row.username ?? 'Unknown' }}
                  <span v-if="row.isMe" class="rk-you-pill">You</span>
                </span>
                <span class="rk-leader" aria-hidden="true"></span>
                <span class="rk-val">{{ fmt(row.value) }}</span>
              </div>
            </template>

            <!-- on the board's roll but with nothing to show for it yet -->
            <div v-else-if="unranked && board.top.length" class="rk-line rk-line-me rk-line-cut">
              <span class="rk-no">No. &mdash;</span>
              <span class="rk-name">Your mark <span class="rk-you-pill">You</span></span>
              <span class="rk-leader" aria-hidden="true"></span>
              <span class="rk-val">{{ fmt(board.me.value) }}</span>
            </div>
          </div>

          <p v-if="board.top.length === 0" class="font-body italic text-text-dim text-sm">
            No one has made their mark yet. The first page of history is blank.
          </p>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped>
.rk-chip {
  display: inline-flex; align-items: baseline;
  padding: 4px 11px;
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--color-text-dim);
  border: 1px solid var(--color-border); border-radius: 999px;
  background: rgba(255,255,255,0.02);
}

.rk-era {
  padding: 1.2rem 1.4rem 3.4rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 35%, transparent);
  border-radius: 12px;
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(200,162,76,0.05), transparent 60%),
    var(--color-surface);
}

/* ── ladder rail ── */
.rk-rail-wrap { padding: 2.6rem 2.2rem 0.4rem; }
.rk-rail {
  position: relative; height: 4px; border-radius: 2px;
  background: var(--color-bg); border: 1px solid var(--color-border);
}
.rk-rail-fill {
  position: absolute; inset: 0 auto 0 0; border-radius: 2px;
  background: linear-gradient(90deg, var(--color-gold), var(--color-gold-bright));
  box-shadow: 0 0 8px rgba(227,200,120,0.35);
  transition: width 700ms cubic-bezier(0.3, 0.8, 0.3, 1);
}
.rk-you {
  position: absolute; top: 50%; width: 13px; height: 13px;
  transform: translate(-50%, -50%) rotate(45deg);
  background: radial-gradient(circle at 35% 30%, #fff6dc, var(--color-gold-bright) 55%, var(--color-gold));
  border: 1px solid rgba(20,12,4,0.8); border-radius: 2px;
  box-shadow: 0 0 10px rgba(227,200,120,0.8);
  z-index: 3;
  animation: rk-you-pulse 2.4s ease-in-out infinite;
}
@keyframes rk-you-pulse {
  0%, 100% { box-shadow: 0 0 8px rgba(227,200,120,0.6); }
  50% { box-shadow: 0 0 16px rgba(227,200,120,1); }
}
.rk-node { position: absolute; top: 50%; z-index: 2; }
.rk-node-diamond {
  position: absolute; top: 0; left: 0; width: 8px; height: 8px;
  transform: translate(-50%, -50%) rotate(45deg);
  background: var(--color-surface-2); border: 1px solid rgba(200,162,76,0.4); border-radius: 1px;
}
.rk-node-hit .rk-node-diamond { background: var(--color-gold); border-color: var(--color-gold-bright); }
.rk-node-name {
  position: absolute; top: 10px; left: 0; transform: translateX(-50%);
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase;
  white-space: nowrap;
}
.rk-node-req {
  position: absolute; top: 24px; left: 0; transform: translateX(-50%);
  font-family: var(--font-body, serif); font-size: 9px; color: color-mix(in srgb, var(--color-text-dim) 70%, transparent);
  white-space: nowrap;
}
.rk-node-tip {
  position: absolute; bottom: 14px; left: 0; transform: translateX(-50%);
  min-width: 130px; padding: 7px 10px;
  font-family: var(--font-body, serif); font-size: 11px; line-height: 1.45; text-align: left;
  background: var(--color-surface-2); border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  border-radius: 6px; box-shadow: 0 8px 20px rgba(0,0,0,0.6);
  opacity: 0; pointer-events: none; transition: opacity 150ms ease;
  z-index: 10;
}
.rk-node:hover .rk-node-tip { opacity: 1; }
/* zig-zag: every other tier labels ABOVE the rail so tight thresholds never collide */
.rk-node-alt .rk-node-name { top: auto; bottom: 10px; }
.rk-node-alt .rk-node-req { top: auto; bottom: 24px; }
.rk-node-alt .rk-node-tip { bottom: auto; top: 14px; }
/* edge nodes: anchor labels inward so they never spill or collide */
.rk-node-first .rk-node-name, .rk-node-first .rk-node-req { transform: none; left: -4px; text-align: left; }
.rk-node-last .rk-node-name, .rk-node-last .rk-node-req { transform: translateX(-100%); left: 4px; text-align: right; }

/* ── TOP THREE — one gold-framed banner strip: [ II | I | III ] ─────────── */
.rk-top3 {
  display: grid; grid-template-columns: 1fr 1.25fr 1fr;
  align-items: stretch;
  margin-bottom: 1rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 35%, transparent);
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(0,0,0,0.16)), var(--color-surface);
  overflow: hidden;
}
.rk-cell {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
}
.rk-cell + .rk-cell { border-left: 1px solid color-mix(in srgb, var(--color-gold) 18%, transparent); }
.rk-cell-1 {
  background: radial-gradient(120% 90% at 50% 0%, rgba(200, 162, 76, 0.14), transparent 72%);
  animation: rk-crown 3s ease-in-out infinite;
}
.rk-cell-me { box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--color-gold) 55%, transparent); }
@keyframes rk-crown {
  0%, 100% { box-shadow: inset 0 0 18px rgba(227,200,120,0.06); }
  50% { box-shadow: inset 0 0 30px rgba(227,200,120,0.18); }
}
.rk-medal { flex: none; display: grid; place-items: center; }
.rk-medal-1 { width: 56px; height: 56px; filter: drop-shadow(0 0 12px rgba(227,200,120,0.5)); }
.rk-medal-2 { width: 42px; height: 42px; filter: drop-shadow(0 0 7px rgba(200,205,215,0.3)); }
.rk-medal-3 { width: 42px; height: 42px; filter: drop-shadow(0 0 7px rgba(200,140,80,0.3)); }
.rk-cell-name {
  font-family: var(--font-heading, serif);
  font-size: 15px; line-height: 1.2;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.rk-cell-1 .rk-cell-name { font-size: 17px; }
.rk-cell-score {
  font-family: var(--font-heading, serif);
  font-size: 17px; line-height: 1.2;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.rk-cell-1 .rk-cell-score { font-size: 21px; }
.rk-cell-unit {
  font-family: var(--font-label, monospace);
  font-size: 8px; font-weight: 600;
  letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--color-text-dim);
}

/* ── the book — rows 4+ as tight account-book lines with dot leaders ────── */
.rk-book {
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background:
    repeating-linear-gradient(180deg, transparent 0 37px, rgba(255, 255, 255, 0.016) 37px 74px),
    var(--color-surface);
  padding: 4px 0;
}
.rk-line {
  display: flex; align-items: baseline; gap: 12px;
  padding: 8px 18px;
  transition: background 130ms ease;
}
.rk-line + .rk-line { border-top: 1px solid color-mix(in srgb, var(--color-border) 55%, transparent); }
.rk-line:hover { background: rgba(200, 162, 76, 0.045); }
.rk-no {
  flex: none; width: 58px;
  font-family: var(--font-label, monospace);
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--color-text-dim);
}
.rk-name {
  flex: none; max-width: 55%;
  font-family: var(--font-body, serif);
  font-size: 15px;
  color: var(--color-text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* the ledger's dot leader — fills the line the way an account book does */
.rk-leader {
  flex: 1 1 auto;
  min-width: 24px;
  align-self: center;
  height: 2px;
  background-image: radial-gradient(circle, color-mix(in srgb, var(--color-gold) 38%, transparent) 1px, transparent 1.2px);
  background-size: 7px 2px;
  background-repeat: repeat-x;
  background-position: left center;
  opacity: 0.7;
}
.rk-val {
  flex: none;
  font-family: var(--font-heading, serif);
  font-size: 16px;
  color: var(--color-gold);
  font-variant-numeric: tabular-nums;
}
.rk-line-me {
  background: linear-gradient(90deg, rgba(200, 162, 76, 0.08), transparent 60%);
  box-shadow: inset 3px 0 0 var(--color-gold);
}
.rk-line-me .rk-name { color: var(--color-gold-bright); }
/* your out-of-frame entry, separated like a torn-off stub */
.rk-line-cut { border-top: 1px dashed color-mix(in srgb, var(--color-gold) 40%, transparent) !important; margin-top: 4px; }
/* the book skipping ahead — pages of names the clerk didn't bother to copy */
.rk-skip {
  display: flex; align-items: center; justify-content: center;
  height: 22px; margin: 3px 0 1px;
  border-top: 1px dashed color-mix(in srgb, var(--color-gold) 28%, transparent);
}
.rk-skip-dots {
  font-family: var(--font-heading, serif);
  font-size: 18px; line-height: 1;
  letter-spacing: 0.2em;
  color: color-mix(in srgb, var(--color-gold) 55%, transparent);
}
.rk-you-pill {
  display: inline-block; margin-left: 6px; padding: 1px 7px;
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--color-bg); background: var(--color-gold); border-radius: 999px;
  vertical-align: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .rk-you, .rk-cell-1 { animation: none; }
  .rk-rail-fill, .rk-line { transition: none; }
}

/* ── vertical tier ladder (mobile <640px) — the horizontal rail can't fit
   the tier labels in one screen width, so on phones we list them top-down
   and the page only ever scrolls vertically. ── */
/* hidden by default (scoped rule beats Tailwind's sm:hidden); shown <640px below */
.rk-vlad { display: none; flex-direction: column; margin-top: 0.5rem; }
@media (max-width: 639.98px) { .rk-vlad { display: flex; } }
.rk-vrow {
  display: flex; align-items: center; gap: 12px;
  padding: 9px 4px;
  border-bottom: 1px solid color-mix(in srgb, var(--color-border) 55%, transparent);
}
.rk-vrow:last-child { border-bottom: 0; }
.rk-vrow-cur {
  background: rgba(200, 162, 76, 0.06);
  border-left: 2px solid var(--color-gold);
  padding-left: 8px; margin-inline: -4px; padding-right: 8px;
}
.rk-vdot {
  flex: none; width: 11px; height: 11px; transform: rotate(45deg);
  border: 1px solid var(--color-border); background: rgba(0, 0, 0, 0.4);
}
.rk-vdot-hit { background: var(--color-gold); border-color: var(--color-gold-bright); }
.rk-vname {
  font-family: var(--font-heading, serif); font-size: 14px; letter-spacing: 0.04em;
  display: flex; align-items: center; gap: 8px;
}
.rk-vyou {
  font-family: var(--font-label, sans-serif); font-size: 8px; font-weight: 700;
  letter-spacing: 0.16em; text-transform: uppercase;
  padding: 2px 6px; color: #241a08; background: var(--color-gold); border-radius: 2px;
}
.rk-vrew {
  font-family: var(--font-body, serif); font-size: 11px; color: var(--color-text-dim);
  margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.rk-vreq {
  flex: none; font-family: var(--font-heading, serif); font-size: 13px;
  color: var(--color-text-dim); font-variant-numeric: tabular-nums;
}

/* ── MOBILE ── podium banner stacks with the winner on top (DOM order II, I, III) */
@media (max-width: 700px) {
  .rk-era { padding-inline: 1rem; }
  .rk-top3 { display: flex; flex-direction: column; }
  .rk-cell:nth-child(2) { order: -1; }
  .rk-cell + .rk-cell { border-left: 0; }
  .rk-cell:nth-child(1), .rk-cell:nth-child(3) {
    border-top: 1px solid color-mix(in srgb, var(--color-gold) 18%, transparent);
  }
}
</style>
